import ClearIcon from '@mui/icons-material/Clear';
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import {
  PayoutFilterValues,
  PayoutMethod,
  PayoutStage,
  PayoutStatus,
} from './types';

interface WorklistFiltersProps {
  value: PayoutFilterValues;
  onChange: (value: PayoutFilterValues) => void;
  onReset: () => void;
  resetDisabled?: boolean;
  showStatus?: boolean;
}

const useStyles = makeStyles<{ showStatus: boolean }>()((theme: Theme, { showStatus }) => ({
  container: {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: showStatus
      ? 'repeat(3, minmax(160px, 180px)) auto'
      : 'repeat(2, minmax(160px, 180px)) auto',
    justifyContent: 'start',
    marginBottom: '16px',

    '@media (max-width: 1100px)': {
      gridTemplateColumns: 'repeat(2, minmax(160px, 180px)) auto',
    },

    '@media (max-width: 700px)': {
      gridTemplateColumns: '1fr',
    },
  },
  clearCell: {
    display: 'flex',
    alignItems: 'center',
  },
  selectRoot: {
    '& .MuiInputBase-root': {
      color: theme.palette.common.white,
      borderRadius: '10px',
      backgroundColor: theme.palette.primary.light,
    },
    '& .MuiSelect-select': {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.light,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.dark,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.info.main,
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
  },
  label: {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: `${theme.palette.text.secondary} !important`,
    },
  },
  menuPaper: {
    backgroundColor: theme.palette.secondary.dark,
    color: theme.palette.common.white,

    '& .MuiMenuItem-root': {
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      fontSize: '14px',

      '&.Mui-selected': {
        backgroundColor: 'rgba(255, 255, 255, 0.16)',
      },

      '&.Mui-selected:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      },

      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
  },
  clearIconContainer: {
    width: '36px',
    height: '36px',
    color: '#FFFFFF',
    borderRadius: '8px',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.35)',
    },
  },
}));

const STAGE_OPTIONS: PayoutStage[] = ['All', 'Onboarding', 'Funding', 'Distribution'];
const STATUS_OPTIONS: Array<{ id: PayoutStatus; label: string }> = [
  { id: 'All Statuses', label: 'All Statuses' },
  { id: 'INVITE_SENT', label: 'Invite Sent' },
  { id: 'IN_PROGRESS', label: 'In Progress' },
  { id: 'COMPLETED', label: 'Completed' },
  { id: 'BLOCKED', label: 'Blocked' },
];
const METHOD_OPTIONS: PayoutMethod[] = ['All', 'ACH', 'Check', 'V', 'Not Applicable'];

const WorklistFilters = ({ value, onChange, onReset, resetDisabled, showStatus = true }: WorklistFiltersProps) => {
  const { classes } = useStyles({ showStatus });
  const menuProps = {
    PaperProps: {
      className: classes.menuPaper,
    },
  };

  const handleFieldChange = (field: keyof PayoutFilterValues) => (event: SelectChangeEvent<string>) => {
    onChange({
      ...value,
      [field]: event.target.value,
    });
  };

  return (
    <Box className={classes.container}>
      <FormControl size="small" className={classes.selectRoot}>
        <InputLabel className={classes.label}>Stage</InputLabel>
        <Select
          label="Stage"
          value={value.stage}
          onChange={handleFieldChange('stage')}
          aria-label="Filter by stage"
          MenuProps={menuProps}
        >
          {STAGE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showStatus && (
        <FormControl size="small" className={classes.selectRoot}>
          <InputLabel className={classes.label}>Onboarding Status</InputLabel>
          <Select
            label="Onboarding Status"
            value={value.status}
            onChange={handleFieldChange('status')}
            aria-label="Filter by onboarding status"
            MenuProps={menuProps}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl size="small" className={classes.selectRoot}>
        <InputLabel className={classes.label}>Method</InputLabel>
        <Select
          label="Method"
          value={value.method}
          onChange={handleFieldChange('method')}
          aria-label="Filter by method"
          MenuProps={menuProps}
        >
          {METHOD_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box className={classes.clearCell}>
        <IconButton
          disableRipple
          className={classes.clearIconContainer}
          aria-label="Clear payout filters"
          disabled={resetDisabled}
          onClick={onReset}
        >
          <ClearIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default WorklistFilters;
